// src/pages/AdminPage.js — COMPLETE with all tabs + mobile admin + access codes restored
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { useAdminPresence } from '../components/PresenceIndicator'
import VaultUploader from '../components/VaultUploader'
import AMAPanel from '../components/AMAPanel'
import ImageSettingsPanel from '../components/ImageSettingsPanel'
import {
  supabase, sendMessage, SKY_BRI_USER_ID,
  deleteVaultContent, getVaultContent,
  createWeeklyQuestion, getActiveQuestion, getQuestionResponses, replyToResponse,
  createAnnouncement, deleteAnnouncement, getAnnouncements,
  createAccessCode, getAccessCodes, deleteAccessCode,
  markConversationRead
} from '../lib/supabase'
import Navbar from '../components/Navbar'
import ChatPhotoViewer from '../components/ChatPhotoViewer'
import { JumpToLatest, TypingIndicator } from '../components/ChatThread'
import useChatScroll from '../hooks/useChatScroll'
import useTyping from '../hooks/useTyping'
import {
  withChatLayout, makePending, resolvePending, failPending, markSending, timeLabel,
  applyReadReceipt, seenMessageId, buildInbox, toLast, previewText, inboxTime,
} from '../lib/chatUtils'
import './AdminPage.css'

const ADMIN_ID = process.env.REACT_APP_ADMIN_ID || ''
const TABS = ['messages','vault','questions','announcements','ama','codes','subscribers','images']
const TAB_LABELS = {
  messages:'Messages', vault:'Vault', questions:'Weekly Q',
  announcements:'Events', ama:'Ask Me Anything', codes:'Access Codes',
  subscribers:'Subscribers', images:'Images'
}
const TAB_ICONS = {
  messages:'💬', vault:'🔐', questions:'❓', announcements:'📣',
  ama:'✨', codes:'🎫', subscribers:'👥', images:'📷'
}

export default function AdminPage() {
  const { session } = useAuth()
  const toast       = useToast()
  const isAdmin     = session?.user?.id === ADMIN_ID
  const [tab, setTab]       = useState('messages')
  const [amaCount, setAmaCount] = useState(0)
  const [unreadMsgs, setUnreadMsgs] = useState(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useAdminPresence(isAdmin)

  useEffect(() => {
    if (!isAdmin) return
    supabase.from('ama_questions').select('id', { count: 'exact' })
      .eq('answered', false)
      .then(({ count }) => setAmaCount(count || 0))
  }, [isAdmin])

  if (!isAdmin) return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12,background:'#080706'}}>
      <p style={{color:'var(--cream)',fontSize:16}}>Access denied.</p>
    </div>
  )

  function switchTab(t) { setTab(t); setMobileMenuOpen(false) }

  return (
    <div className="admin-page">
      <Navbar />

      {/* Verified admin badge */}
      <div className="admin-verified-bar">
        <span className="admin-verified-badge">✦ VERIFIED</span>
        <span className="admin-verified-name">Sky Bri · Admin Dashboard</span>
      </div>

      {/* MOBILE MENU TOGGLE */}
      <div className="admin-mobile-header">
        <span className="admin-mobile-title">
          {TAB_ICONS[tab]} {TAB_LABELS[tab]}
        </span>
        <button
          className="admin-mobile-menu-btn"
          onClick={() => setMobileMenuOpen(o => !o)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* DESKTOP TABS + MOBILE DRAWER */}
      <div className={`admin-top-tabs ${mobileMenuOpen ? 'admin-top-tabs--open' : ''}`}>
        {TABS.map(t => (
          <button key={t}
            className={`admin-top-tab ${tab===t ? 'admin-top-tab--active' : ''}`}
            onClick={() => switchTab(t)}>
            <span className="admin-top-tab__icon">{TAB_ICONS[t]}</span>
            <span>{TAB_LABELS[t]}</span>
            {t==='ama' && amaCount>0 && (
              <span className="admin-tab-badge">{amaCount}</span>
            )}
            {t==='messages' && unreadMsgs>0 && (
              <span className="admin-tab-badge">{unreadMsgs}</span>
            )}
          </button>
        ))}
      </div>

      {mobileMenuOpen && <div className="admin-backdrop" onClick={() => setMobileMenuOpen(false)} />}

      <div className="admin-content">
        {tab === 'messages'      && <MessagesPanel adminId={ADMIN_ID} toast={toast} onUnread={setUnreadMsgs} />}
        {tab === 'vault'         && <VaultPanel toast={toast} />}
        {tab === 'questions'     && <QuestionsPanel toast={toast} />}
        {tab === 'announcements' && <AnnouncementsPanel toast={toast} />}
        {tab === 'ama'           && <AMAPanel toast={toast} />}
        {tab === 'codes'         && <AccessCodesPanel toast={toast} />}
        {tab === 'subscribers'   && <SubscribersPanel adminId={ADMIN_ID} toast={toast} />}
        {tab === 'images'        && <ImageSettingsPanel toast={toast} />}
      </div>
    </div>
  )
}

// ─── MESSAGES ─────────────────────────────────────────────
function MessagesPanel({ adminId, toast, onUnread }) {
  const [subs, setSubs]         = useState([])
  const [selected, setSelected] = useState(null)
  const [messages, setMessages] = useState([])
  const [unread, setUnread]     = useState({})
  const [reply, setReply]       = useState('')
  const [loading, setLoading]   = useState(true)
  const [search, setSearch]     = useState('')
  const [showList, setShowList] = useState(true) // mobile: show list or convo
  const [viewerSrc, setViewerSrc] = useState(null)
  const [lastMsg, setLastMsg]  = useState({}) // { [memberId]: { content, created_at, fromMe } }
  const selectedRef = useRef(null)
  selectedRef.current = selected
  const { otherTyping, notifyTyping, clearTyping } = useTyping(selected?.id, adminId)
  const { containerRef, bottomRef, onScroll, showJump, jumpToLatest } =
    useChatScroll(messages, adminId, selected?.id, otherTyping)

  useEffect(() => {
    loadSubs()
    loadInbox()
    const sub = supabase.channel('admin-inbox')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'messages',filter:`receiver_id=eq.${adminId}`},
        payload => {
          const msg = payload.new
          setLastMsg(l => ({ ...l, [msg.sender_id]: toLast(msg, adminId) }))
          if (selectedRef.current?.id === msg.sender_id) {
            clearTyping()
            // The conversation is open in front of me, so this counts as read
            if (!document.hidden) markConversationRead(msg.sender_id)
          }
          setSelected(prev => {
            if (prev?.id === msg.sender_id) {
              setMessages(m => [...m, msg])
            } else {
              setUnread(u => {
                const next = {...u, [msg.sender_id]: (u[msg.sender_id]||0)+1}
                const total = Object.values(next).reduce((a,b)=>a+b,0)
                if (onUnread) onUnread(total)
                return next
              })
              toast.show('New message','gold')
            }
            return prev
          })
        })
      // A member read one of my replies -> show "Seen" instantly
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'messages',filter:`sender_id=eq.${adminId}`},
        payload => setMessages(m => applyReadReceipt(m, payload.new)))
      .subscribe()
    // Coming back to this tab with a conversation open = I've now read it
    const onVisible = () => {
      if (!document.hidden && selectedRef.current) markConversationRead(selectedRef.current.id)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => { sub.unsubscribe(); document.removeEventListener('visibilitychange', onVisible) }
  }, []) // eslint-disable-line

  // Latest message + unread count per member, straight from the database —
  // so it survives a refresh (and the inbox can sort by who wrote last)
  async function loadInbox() {
    const { data } = await supabase.from('messages')
      .select('id,sender_id,receiver_id,content,created_at,read_at')
      .or(`sender_id.eq.${adminId},receiver_id.eq.${adminId}`)
      .order('created_at',{ascending:false})
      .limit(1000)
    if (!data) return
    const { last, unread: counts } = buildInbox(data, adminId)
    setLastMsg(last)
    setUnread(counts)
    if (onUnread) onUnread(Object.values(counts).reduce((a,b)=>a+b,0))
  }

  async function loadSubs() {
    setLoading(true)
    const { data } = await supabase.from('profiles').select('*').neq('id',adminId).order('created_at',{ascending:false})
    if (data) setSubs(data)
    setLoading(false)
  }

  async function openConvo(user) {
    setSelected(user)
    setMessages([]) // don't flash the previous member's conversation while this one loads
    const nextUnread = {...unread,[user.id]:0}
    setUnread(nextUnread)
    if (onUnread) onUnread(Object.values(nextUnread).reduce((a,b)=>a+b,0))
    setShowList(false) // mobile: switch to convo view
    const { data } = await supabase.from('messages').select('*')
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order('created_at',{ascending:true})
    if (data) setMessages(data)
    // Opening the conversation = reading it (this is what members see as "Seen")
    markConversationRead(user.id)
  }

  // Send to the server and settle the on-screen bubble either way
  async function deliver(tempId, content, toId) {
    const { data:msg, error } = await sendMessage({ senderId:adminId, receiverId:toId, content })
    if (error || !msg) {
      setMessages(prev => failPending(prev, tempId))
      toast.show('Message not sent — tap Retry','error')
    } else {
      setMessages(prev => resolvePending(prev, tempId, msg))
      setLastMsg(l => ({ ...l, [toId]: toLast(msg, adminId) }))
    }
  }

  function handleSend() {
    const content = reply.trim()
    if (!content || !selected) return
    setReply('')
    const pending = makePending({ senderId:adminId, receiverId:selected.id, content })
    setMessages(prev => [...prev, pending])
    deliver(pending.id, content, selected.id)
  }

  function retryMessage(id) {
    const m = messages.find(x => x.id === id)
    if (!m) return
    setMessages(prev => markSending(prev, id))
    deliver(id, m.content, m.receiver_id)
  }
  function discardMessage(id) { setMessages(prev => prev.filter(x => x.id !== id)) }

  const tc = { gold:'var(--gold)', premium:'var(--plat)', platinum:'var(--plat-bright)' }
  const totalUnread = Object.values(unread).reduce((a,b)=>a+b,0)
  const filtered = subs
    .filter(u =>
      u.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()))
    // whoever wrote (or was written to) most recently goes to the top;
    // members with no messages keep their signup order underneath
    .sort((a, b) => {
      const ta = lastMsg[a.id]?.created_at || ''
      const tb = lastMsg[b.id]?.created_at || ''
      return ta === tb ? 0 : ta < tb ? 1 : -1
    })
  const seenId = selected ? seenMessageId(messages, adminId) : null

  function isImgMsg(c) { return c?.startsWith('[IMAGE]') }
  function getImgUrl(c) { return c?.replace('[IMAGE]','') }

  return (
    <div className="admin-messages-panel">
      {/* SIDEBAR */}
      <div className={`admin-sidebar ${!showList ? 'admin-sidebar--hidden-mobile' : ''}`}>
        <div className="admin-sidebar__header">
          <div className="admin-sidebar__top">
            <div>
              <p className="admin-sidebar__title">Subscribers</p>
              <p className="admin-sidebar__count">{subs.length} members</p>
            </div>
            {totalUnread > 0 && <span className="admin-unread-total">{totalUnread} new</span>}
          </div>
          <input className="admin-search" placeholder="Search members..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {loading && <p className="admin-sidebar__loading">Loading...</p>}
        {filtered.map(u => (
          <button key={u.id}
            className={`admin-sub-row ${selected?.id===u.id ? 'admin-sub-row--active' : ''}`}
            onClick={() => openConvo(u)}>
            <div className="admin-sub-row__avatar">
              {u.avatar_url
                ? <img src={u.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} />
                : u.display_name?.[0]?.toUpperCase()||'?'}
            </div>
            <div className="admin-sub-row__info">
              <span className="admin-sub-row__name">{u.display_name||'Member'}</span>
              <span className="admin-sub-row__tier" style={{color:tc[u.tier]}}>
                {u.tier?.charAt(0).toUpperCase()+u.tier?.slice(1)}
              </span>
              {lastMsg[u.id] && (
                <span className={`admin-sub-row__preview ${unread[u.id] > 0 ? 'admin-sub-row__preview--unread' : ''}`}>
                  {previewText(lastMsg[u.id])}
                </span>
              )}
            </div>
            <div className="admin-sub-row__meta">
              {lastMsg[u.id] && <span className="admin-sub-row__time">{inboxTime(lastMsg[u.id].created_at)}</span>}
              {unread[u.id] > 0 && <span className="admin-unread-badge">{unread[u.id]}</span>}
            </div>
          </button>
        ))}
      </div>

      {/* CONVERSATION */}
      <div className={`admin-main ${showList ? 'admin-main--hidden-mobile' : ''}`}>
        {!selected ? (
          <div className="admin-main__empty">
            <p>Select a subscriber to view their conversation</p>
            {totalUnread > 0 && <p style={{color:'var(--gold)',fontSize:13,marginTop:8}}>{totalUnread} unread</p>}
          </div>
        ) : (
          <>
            <div className="admin-main__header">
              {/* Back button for mobile */}
              <button className="admin-back-btn" onClick={() => setShowList(true)}>← Back</button>
              <div className="admin-main__header-avatar">
                {selected.avatar_url
                  ? <img src={selected.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} />
                  : selected.display_name?.[0]?.toUpperCase()||'?'}
              </div>
              <div>
                <p className="admin-main__name">{selected.display_name}</p>
                <p className="admin-main__meta">
                  <span style={{color:tc[selected.tier]}}>
                    {selected.tier?.charAt(0).toUpperCase()+selected.tier?.slice(1)} Card
                  </span>
                  {selected.birthday && ` · 🎂 ${new Date(selected.birthday).toLocaleDateString('en-US',{month:'short',day:'numeric'})}`}
                  {selected.location && ` · 📍 ${selected.location}`}
                </p>
                {selected.bio && <p style={{fontSize:11,color:'var(--dim)',marginTop:2}}>{selected.bio}</p>}
              </div>
            </div>

            <div className="admin-messages" ref={containerRef} onScroll={onScroll}>
              {messages.length===0 && <p className="admin-messages__empty">No messages yet</p>}
              {withChatLayout(messages).map(row => {
                if (row.type === 'divider') {
                  return <div key={row.key} className="chat-day"><span>{row.label}</span></div>
                }
                const msg     = row.msg
                const fromSub = msg.sender_id === selected.id
                const isImg   = isImgMsg(msg.content)
                const status  = msg._status
                return (
                  <div key={row.key} className={`admin-msg ${fromSub?'admin-msg--sub':'admin-msg--sky'} ${status==='sending'?'admin-msg--pending':''}`}>
                    <div className="admin-msg__sender">
                      {fromSub ? selected.display_name : '✦ You (Sky Bri)'}
                    </div>
                    <div className="admin-msg__bubble">
                      {isImg
                        ? <img src={getImgUrl(msg.content)} alt="img"
                            style={{maxWidth:200,maxHeight:200,objectFit:'cover',cursor:'pointer',borderRadius:2}}
                            onClick={() => setViewerSrc(getImgUrl(msg.content))} />
                        : msg.content}
                    </div>
                    <div className={`admin-msg__time ${status==='failed'?'admin-msg__time--failed':''}`}>
                      {status === 'failed' ? (
                        <>Not sent · <button type="button" onClick={() => retryMessage(msg.id)}>Retry</button> · <button type="button" onClick={() => discardMessage(msg.id)}>Delete</button></>
                      ) : status === 'sending' ? 'Sending…' : (
                        <>{timeLabel(msg.created_at)}{msg.id === seenId && <span className="chat-seen"> · Seen</span>}</>
                      )}
                    </div>
                  </div>
                )
              })}
              <TypingIndicator show={otherTyping} name={selected.display_name || 'Member'} />
              <JumpToLatest show={showJump} onClick={jumpToLatest} />
              <div ref={bottomRef} />
            </div>

            <div className="admin-reply">
              <textarea className="admin-reply__input"
                placeholder={`Reply to ${selected.display_name}...`}
                value={reply}
                onChange={e => { setReply(e.target.value); if (e.target.value) notifyTyping() }}
                onKeyDown={e => { if(e.key==='Enter'&&(e.ctrlKey||e.metaKey)) handleSend() }}
                rows={3}
              />
              <div className="admin-reply__row">
                <button className="btn btn--gold" onClick={handleSend} disabled={!reply.trim()}>
                  Send ↑
                </button>
                <p className="admin-reply__hint">Ctrl+Enter to send</p>
              </div>
            </div>
          </>
        )}
      </div>
      <ChatPhotoViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />
    </div>
  )
}

// ─── ACCESS CODES ─────────────────────────────────────────
const PAYMENT_METHODS = ['Apple Gift Card','Visa Gift Card','Razer Gold','CashApp']

function AccessCodesPanel({ toast }) {
  const [codes, setCodes]       = useState([])
  const [loading, setLoading]   = useState(true)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied]     = useState(null)
  const [filter, setFilter]     = useState('unused')
  const [form, setForm] = useState({
    tier:'gold', paymentMethod:'CashApp', amountPaid:'',
    memberEmail:'', note:'', expiresAt:''
  })

  useEffect(() => { loadCodes() }, [])

  async function loadCodes() {
    setLoading(true)
    const { data } = await getAccessCodes()
    if (data) setCodes(data)
    setLoading(false)
  }

  async function handleGenerate(e) {
    e.preventDefault()
    setGenerating(true)
    const { data, error, code } = await createAccessCode({
      tier:          form.tier,
      paymentMethod: form.paymentMethod,
      amountPaid:    form.amountPaid ? parseFloat(form.amountPaid) : null,
      note:          form.note || (form.memberEmail ? `For: ${form.memberEmail}` : null),
      expiresAt:     form.expiresAt || null
    })
    setGenerating(false)
    if (error) { toast.show('Failed to generate code','error'); return }
    toast.show(`Code generated: ${code}`,'success')
    // Auto-copy to clipboard
    navigator.clipboard.writeText(code).catch(()=>{})
    setCopied(code)
    setTimeout(() => setCopied(null), 5000)
    setForm({tier:'gold',paymentMethod:'CashApp',amountPaid:'',memberEmail:'',note:'',expiresAt:''})
    loadCodes()
  }

  async function handleDelete(id) {
    const { error } = await deleteAccessCode(id)
    if (error) { toast.show('Failed to delete','error'); return }
    setCodes(prev => prev.filter(c => c.id!==id))
    toast.show('Code deleted','success')
  }

  async function copyCode(code) {
    await navigator.clipboard.writeText(code).catch(()=>{})
    setCopied(code)
    setTimeout(() => setCopied(null), 2500)
  }

  const tc = { gold:'var(--gold)', premium:'var(--plat)', platinum:'var(--plat-bright)' }
  const filtered = codes.filter(c => {
    if (filter==='unused') return !c.used
    if (filter==='used')   return c.used
    return true
  })
  const stats = {
    total: codes.length,
    unused: codes.filter(c=>!c.used).length,
    used: codes.filter(c=>c.used).length
  }

  return (
    <div className="admin-feature-panel">
      <div className="admin-feature-panel__form">
        <h2 className="admin-feature-title">Generate Access Code</h2>
        <p className="admin-feature-sub">
          When a member pays, generate their unique code here. It is automatically copied to your clipboard so you can paste it and send it directly.
        </p>

        <form onSubmit={handleGenerate} className="admin-form">
          <div className="admin-form-row">
            <div className="field">
              <label>Tier</label>
              <select value={form.tier} onChange={e => setForm(f=>({...f,tier:e.target.value}))}>
                <option value="gold">Gold — $150</option>
                <option value="premium">Premium — $500</option>
                <option value="platinum">Platinum — $1,000</option>
              </select>
            </div>
            <div className="field">
              <label>Payment Method</label>
              <select value={form.paymentMethod} onChange={e => setForm(f=>({...f,paymentMethod:e.target.value}))}>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="admin-form-row">
            <div className="field">
              <label>Amount Paid ($)</label>
              <input type="number" placeholder="e.g. 150"
                value={form.amountPaid} onChange={e => setForm(f=>({...f,amountPaid:e.target.value}))} />
            </div>
            <div className="field">
              <label>Member Email (optional)</label>
              <input type="email" placeholder="their@email.com"
                value={form.memberEmail} onChange={e => setForm(f=>({...f,memberEmail:e.target.value}))} />
            </div>
          </div>

          <div className="field">
            <label>Note (your private reference)</label>
            <input type="text" placeholder="e.g. Paid via CashApp — @username"
              value={form.note} onChange={e => setForm(f=>({...f,note:e.target.value}))} />
          </div>

          <button className="btn btn--gold" type="submit" disabled={generating} style={{width:'100%'}}>
            {generating ? 'Generating...' : 'Generate & Copy Code'}
          </button>
        </form>

        {/* Last generated code — big display for easy copying */}
        {copied && (
          <div className="ac-generated">
            <p className="ac-generated__label">Code generated — copied to clipboard!</p>
            <div className="ac-generated__code">{copied}</div>
            <p className="ac-generated__hint">
              Paste this code and send it to the member via text, WhatsApp, or Zangi.
            </p>
          </div>
        )}

        {/* How it works */}
        <div className="ac-howto">
          <p className="ac-howto__title">How it works</p>
          <div className="ac-howto__steps">
            {[
              'Member pays you via Apple Gift Card, Visa, Razer Gold, or CashApp',
              'You come here and generate their code — it copies automatically',
              'You paste and send the code to them via WhatsApp, text, or Zangi',
              'They enter the code on the signup page — their tier activates instantly'
            ].map((s,i) => (
              <div key={i} className="ac-howto__step">
                <span className="ac-howto__num">{i+1}</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-feature-panel__list">
        {/* Stats */}
        <div className="ac-stats">
          <div className="ac-stat"><span>{stats.total}</span><span>Total</span></div>
          <div className="ac-stat"><span style={{color:'#4CAF7D'}}>{stats.unused}</span><span>Active</span></div>
          <div className="ac-stat"><span style={{color:'var(--dim)'}}>{stats.used}</span><span>Used</span></div>
        </div>

        {/* Filter */}
        <div className="ac-filter">
          {['all','unused','used'].map(f => (
            <button key={f}
              className={`ac-filter-btn ${filter===f?'ac-filter-btn--active':''}`}
              onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase()+f.slice(1)}
            </button>
          ))}
        </div>

        <h3 className="admin-list-title" style={{marginTop:0}}>{filtered.length} codes</h3>
        {loading && <p className="admin-list-empty">Loading...</p>}
        {!loading && filtered.length===0 && <p className="admin-list-empty">No codes found</p>}

        {filtered.map(c => (
          <div key={c.id} className={`ac-row ${c.used?'ac-row--used':'ac-row--active'}`}>
            <div className="ac-row__left">
              <div className="ac-row__code-wrap">
                <span className="ac-row__code">{c.code}</span>
                {!c.used && (
                  <button className="ac-row__copy" onClick={() => copyCode(c.code)}>
                    {copied===c.code ? '✓ Copied' : 'Copy'}
                  </button>
                )}
              </div>
              <div className="ac-row__meta">
                <span style={{color:tc[c.tier],fontSize:11,letterSpacing:1}}>{c.tier}</span>
                {c.payment_method && <span className="ac-row__method">· {c.payment_method}</span>}
                {c.amount_paid && <span className="ac-row__amount">· ${c.amount_paid}</span>}
              </div>
              {c.note && <p className="ac-row__note">{c.note}</p>}
              {c.used && c.profiles && (
                <p className="ac-row__used-by">
                  Used by {c.profiles.display_name||c.profiles.email} ·{' '}
                  {new Date(c.used_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                </p>
              )}
            </div>
            <div className="ac-row__right">
              <span className={`ac-row__status ${c.used?'ac-row__status--used':'ac-row__status--active'}`}>
                {c.used?'Used':'Active'}
              </span>
              {!c.used && (
                <button className="btn btn--ghost btn--sm" onClick={() => handleDelete(c.id)}>Delete</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── VAULT ────────────────────────────────────────────────
function VaultPanel({ toast }) {
  const [items, setItems] = useState([])
  const [thumbUrls, setThumbUrls] = useState({}) // { [item.id]: signedUrl }
  useEffect(() => { loadVault() }, [])
  async function loadVault() {
    const { data } = await getVaultContent()
    if (data) {
      setItems(data)
      // Bucket is private — admin needs a signed URL too, just like members do.
      const photoItems = data.filter(i => i.media_type === 'photo')
      const entries = await Promise.all(
        photoItems.map(async item => {
          const { data: signed } = await supabase.storage
            .from('vault')
            .createSignedUrl(item.media_url, 3600)
          return [item.id, signed?.signedUrl || null]
        })
      )
      setThumbUrls(Object.fromEntries(entries))
    }
  }
  async function handleDelete(id) {
    const { error } = await deleteVaultContent(id)
    if (error) { toast.show('Failed','error'); return }
    setItems(prev => prev.filter(i=>i.id!==id)); toast.show('Removed','success')
  }
  return (
    <div className="admin-feature-panel">
      <div className="admin-feature-panel__form">
        <h2 className="admin-feature-title">Add to Vault</h2>
        <p className="admin-feature-sub">Upload photos or videos directly from your device.</p>
        <VaultUploader toast={toast} onUploaded={loadVault} />
      </div>
      <div className="admin-feature-panel__list">
        <h3 className="admin-list-title">Vault Contents ({items.length})</h3>
        {items.length===0 && <p className="admin-list-empty">No content yet</p>}
        {items.map(item => (
          <div key={item.id} className="admin-list-item">
            <div className="admin-list-item__thumb">
              {item.media_type==='photo'
                ? <img src={thumbUrls[item.id]} alt={item.title} style={{width:48,height:48,objectFit:'cover'}} />
                : <div style={{width:48,height:48,background:'var(--bg-surface)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--gold)',fontSize:18}}>▶</div>}
            </div>
            <div className="admin-list-item__info">
              <p className="admin-list-item__title">{item.title}</p>
              <p className="admin-list-item__meta">{item.media_type} · {item.min_tier} · {new Date(item.created_at).toLocaleDateString()}</p>
            </div>
            <button className="btn btn--ghost btn--sm" onClick={() => handleDelete(item.id)}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── WEEKLY Q ─────────────────────────────────────────────
function QuestionsPanel({ toast }) {
  const [question, setQuestion] = useState(null)
  const [responses, setResponses] = useState([])
  const [newQ, setNewQ] = useState('')
  const [replies, setReplies] = useState({})
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => { loadQuestion() }, [])
  async function loadQuestion() {
    const { data:q } = await getActiveQuestion()
    if (q) { setQuestion(q); const { data:r } = await getQuestionResponses(q.id); if(r) setResponses(r) }
  }
  async function handleNewQ(e) {
    e.preventDefault()
    setSubmitting(true)
    const { error } = await createWeeklyQuestion(newQ.trim())
    setSubmitting(false)
    if (error) { toast.show('Failed','error'); return }
    toast.show('Question set!','success'); setNewQ(''); loadQuestion()
  }
  async function handleReply(responseId) {
    const text = replies[responseId]?.trim(); if (!text) return
    const { error } = await replyToResponse(responseId, text)
    if (error) { toast.show('Failed','error'); return }
    toast.show('Reply sent!','success')
    setReplies(prev => ({...prev,[responseId]:''})); loadQuestion()
  }
  return (
    <div className="admin-feature-panel">
      <div className="admin-feature-panel__form">
        <h2 className="admin-feature-title">Weekly Question</h2>
        <p className="admin-feature-sub">Post a question for all members to answer.</p>
        {question && (
          <div className="admin-current-q">
            <p className="admin-current-q__label">Active question</p>
            <p className="admin-current-q__text">"{question.question}"</p>
            <p className="admin-current-q__meta">{responses.length} responses</p>
          </div>
        )}
        <form onSubmit={handleNewQ} className="admin-form">
          <div className="field">
            <label>New Question</label>
            <input type="text" placeholder="Ask your members something..." value={newQ} onChange={e=>setNewQ(e.target.value)} required />
          </div>
          <button className="btn btn--gold" type="submit" disabled={submitting}>{submitting?'Setting...':'Set This Week\'s Question'}</button>
        </form>
      </div>
      <div className="admin-feature-panel__list">
        <h3 className="admin-list-title">Responses ({responses.length})</h3>
        {responses.length===0 && <p className="admin-list-empty">No responses yet</p>}
        {responses.map(r => (
          <div key={r.id} className="admin-response-item">
            <div className="admin-response-item__header">
              <span className="admin-response-item__name">{r.profiles?.display_name||'Member'}</span>
              <span className="admin-response-item__tier">{r.profiles?.tier}</span>
            </div>
            <p className="admin-response-item__text">"{r.response}"</p>
            {r.sky_reply ? (
              <div className="admin-response-item__replied">
                <span className="admin-response-item__replied-label">Your reply</span>
                <p>{r.sky_reply}</p>
              </div>
            ) : (
              <div className="admin-response-item__reply-form">
                <input type="text" placeholder="Reply..." value={replies[r.id]||''}
                  onChange={e=>setReplies(prev=>({...prev,[r.id]:e.target.value}))}
                  onKeyDown={e=>{if(e.key==='Enter')handleReply(r.id)}}
                  className="admin-inline-input" />
                <button className="btn btn--gold btn--sm" onClick={()=>handleReply(r.id)} disabled={!replies[r.id]?.trim()}>Reply</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── ANNOUNCEMENTS ────────────────────────────────────────
function AnnouncementsPanel({ toast }) {
  const [items, setItems] = useState([])
  const [form, setForm] = useState({title:'',description:'',eventDate:''})
  const [loading, setLoading] = useState(false)
  useEffect(()=>{load()},[])
  async function load(){const{data}=await getAnnouncements();if(data)setItems(data)}
  async function handleAdd(e){
    e.preventDefault(); if(!form.title.trim()){toast.show('Title required','error');return}
    setLoading(true)
    const{error}=await createAnnouncement({title:form.title,description:form.description,eventDate:form.eventDate||null})
    setLoading(false)
    if(error){toast.show('Failed','error');return}
    toast.show('Event announced!','success'); setForm({title:'',description:'',eventDate:''}); load()
  }
  async function handleDelete(id){
    const{error}=await deleteAnnouncement(id)
    if(error){toast.show('Failed','error');return}
    setItems(prev=>prev.filter(i=>i.id!==id)); toast.show('Removed','success')
  }
  return (
    <div className="admin-feature-panel">
      <div className="admin-feature-panel__form">
        <h2 className="admin-feature-title">Events & Countdowns</h2>
        <p className="admin-feature-sub">Announce events with live countdowns on all member dashboards.</p>
        <form onSubmit={handleAdd} className="admin-form">
          <div className="field"><label>Event Title</label>
            <input type="text" placeholder="e.g. Sky Bri goes live" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} required/></div>
          <div className="field"><label>Description (optional)</label>
            <input type="text" placeholder="Brief details..." value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
          <div className="field"><label>Date & Time (optional)</label>
            <input type="datetime-local" value={form.eventDate} onChange={e=>setForm(f=>({...f,eventDate:e.target.value}))}/></div>
          <button className="btn btn--gold" type="submit" disabled={loading}>{loading?'Publishing...':'Announce to All Members'}</button>
        </form>
      </div>
      <div className="admin-feature-panel__list">
        <h3 className="admin-list-title">Active Events ({items.length})</h3>
        {items.length===0&&<p className="admin-list-empty">No active events</p>}
        {items.map(item=>(
          <div key={item.id} className="admin-list-item">
            <div className="admin-list-item__info">
              <p className="admin-list-item__title">{item.title}</p>
              <p className="admin-list-item__meta">{item.event_date?new Date(item.event_date).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}):'No date'}</p>
            </div>
            <button className="btn btn--ghost btn--sm" onClick={()=>handleDelete(item.id)}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── TIER IMPACT (used by the Subscribers tier-switch preview) ──────
const TIER_RANK  = { gold: 1, premium: 2, platinum: 3 }
const TIER_LABEL = { gold: 'Gold', premium: 'Premium', platinum: 'Platinum' }
const TIER_FEATURES = [
  { label: 'Personal Q&A Chat', minTier: 'gold',     detail: t => t === 'gold' ? '24hr window · 4x per month' : 'Unlimited messaging' },
  { label: 'Ask Me Anything',   minTier: 'gold',     detail: t => t === 'gold' ? '1 question per week' : '2 questions per week' },
  { label: 'Exclusive Vault',   minTier: 'gold',     detail: () => 'Private photos & videos' },
  { label: 'Weekly Question',   minTier: 'gold',     detail: () => 'Personal reply from Sky Bri' },
  { label: 'Zangi Messaging',   minTier: 'premium',  detail: () => 'Private Zangi handle' },
  { label: 'Private Number',    minTier: 'premium',  detail: () => 'Text directly' },
  { label: 'Voice Calls',       minTier: 'platinum', detail: () => 'Live audio call' },
  { label: 'Private Dinner',    minTier: 'platinum', detail: () => 'You cover the bill only' },
]
function tierDiff(oldTier, newTier) {
  const oldRank = TIER_RANK[oldTier] || 1
  const newRank = TIER_RANK[newTier] || 1
  const gained = TIER_FEATURES.filter(f => TIER_RANK[f.minTier] > oldRank && TIER_RANK[f.minTier] <= newRank)
  const lost   = TIER_FEATURES.filter(f => TIER_RANK[f.minTier] <= oldRank && TIER_RANK[f.minTier] > newRank)
  const changedDetail = TIER_FEATURES.filter(f =>
    TIER_RANK[f.minTier] <= Math.min(oldRank, newRank) && f.detail(oldTier) !== f.detail(newTier)
  )
  return { gained, lost, changedDetail, direction: newRank === oldRank ? 'same' : newRank > oldRank ? 'up' : 'down' }
}

function TierImpactModal({ member, oldTier, newTier, onCancel, onConfirm, saving }) {
  const { gained, lost, changedDetail, direction } = tierDiff(oldTier, newTier)
  const tc = { gold: 'var(--gold)', premium: 'var(--plat)', platinum: 'var(--plat-bright)' }
  return (
    <div className="tier-impact-overlay" onClick={onCancel}>
      <div className="tier-impact-modal" onClick={e => e.stopPropagation()}>
        <p className="tier-impact-modal__eyebrow">
          {direction === 'up' ? 'Upgrading' : direction === 'down' ? 'Downgrading' : 'Switching'} {member.display_name || 'this member'}
        </p>
        <div className="tier-impact-modal__path">
          <span style={{ color: tc[oldTier] }}>{TIER_LABEL[oldTier]}</span>
          <span className="tier-impact-modal__arrow">→</span>
          <span style={{ color: tc[newTier] }}>{TIER_LABEL[newTier]}</span>
        </div>

        {gained.length > 0 && (
          <div className="tier-impact-modal__section">
            <p className="tier-impact-modal__section-label tier-impact-modal__section-label--gain">Gains instantly</p>
            {gained.map(f => (
              <div key={f.label} className="tier-impact-row">
                <span className="tier-impact-row__mark tier-impact-row__mark--gain">+</span>
                <div>
                  <p className="tier-impact-row__label">{f.label}</p>
                  <p className="tier-impact-row__detail">{f.detail(newTier)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {lost.length > 0 && (
          <div className="tier-impact-modal__section">
            <p className="tier-impact-modal__section-label tier-impact-modal__section-label--lose">Loses access to</p>
            {lost.map(f => (
              <div key={f.label} className="tier-impact-row">
                <span className="tier-impact-row__mark tier-impact-row__mark--lose">−</span>
                <div>
                  <p className="tier-impact-row__label">{f.label}</p>
                  <p className="tier-impact-row__detail">{f.detail(oldTier)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {changedDetail.length > 0 && (
          <div className="tier-impact-modal__section">
            <p className="tier-impact-modal__section-label">Limits change</p>
            {changedDetail.map(f => (
              <div key={f.label} className="tier-impact-row">
                <span className="tier-impact-row__mark">↕</span>
                <div>
                  <p className="tier-impact-row__label">{f.label}</p>
                  <p className="tier-impact-row__detail">{f.detail(oldTier)} → {f.detail(newTier)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {gained.length === 0 && lost.length === 0 && changedDetail.length === 0 && (
          <p className="tier-impact-modal__none">No access changes between these tiers.</p>
        )}

        <p className="tier-impact-modal__note">They'll see this reflected on their dashboard immediately — no re-login needed.</p>

        <div className="tier-impact-modal__actions">
          <button className="btn btn--ghost btn--sm" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn--gold btn--sm" onClick={onConfirm} disabled={saving}>
            {saving ? 'Applying...' : `Confirm ${TIER_LABEL[newTier]}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── SUBSCRIBERS ──────────────────────────────────────────
function SubscribersPanel({ adminId, toast }) {
  const [subs, setSubs]       = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [pending, setPending] = useState(null) // { member, oldTier, newTier }
  const [saving, setSaving]   = useState(false)
  useEffect(()=>{load()},[])
  async function load(){
    setLoading(true)
    const{data}=await supabase.from('profiles').select('*').neq('id',adminId).order('created_at',{ascending:false})
    if(data)setSubs(data); setLoading(false)
  }
  async function confirmTierChange(){
    if(!pending) return
    setSaving(true)
    const{error}=await supabase.from('profiles').update({tier:pending.newTier}).eq('id',pending.member.id)
    setSaving(false)
    if(error){toast.show('Failed to update tier','error');return}
    toast.show(`${pending.member.display_name||'Member'} is now ${TIER_LABEL[pending.newTier]}`,'success')
    setSubs(prev=>prev.map(s=>s.id===pending.member.id?{...s,tier:pending.newTier}:s))
    setPending(null)
  }
  const tc={gold:'var(--gold)',premium:'var(--plat)',platinum:'var(--plat-bright)'}
  const counts={gold:subs.filter(s=>s.tier==='gold').length,premium:subs.filter(s=>s.tier==='premium').length,platinum:subs.filter(s=>s.tier==='platinum').length}
  const filtered=subs.filter(u=>u.display_name?.toLowerCase().includes(search.toLowerCase())||u.email?.toLowerCase().includes(search.toLowerCase()))
  return (
    <div className="admin-subs-panel">
      <div className="admin-subs-stats">
        <div className="admin-stat"><span style={{color:'var(--gold)'}}>{counts.gold}</span><span>Gold</span></div>
        <div className="admin-stat"><span style={{color:'var(--plat)'}}>{counts.premium}</span><span>Premium</span></div>
        <div className="admin-stat"><span style={{color:'var(--plat-bright)'}}>{counts.platinum}</span><span>Platinum</span></div>
        <div className="admin-stat"><span style={{color:'var(--cream)'}}>{subs.length}</span><span>Total</span></div>
      </div>
      <div className="admin-subs-search-wrap">
        <input className="admin-search" style={{maxWidth:340}} placeholder="Search by name or email..."
          value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      {loading&&<p style={{padding:20,color:'var(--muted)',fontSize:13}}>Loading...</p>}
      <div className="admin-subs-table">
        {filtered.map(u=>(
          <div key={u.id} className="admin-subs-row">
            <div className="admin-subs-row__avatar">
              {u.avatar_url
                ? <img src={u.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}}/>
                : u.display_name?.[0]?.toUpperCase()||'?'}
            </div>
            <div className="admin-subs-row__info">
              <p className="admin-subs-row__name">{u.display_name||'Member'}</p>
              <p className="admin-subs-row__email">{u.email}</p>
              {u.bio&&<p style={{fontSize:10,color:'var(--dim)',marginTop:2}}>{u.bio.slice(0,60)}{u.bio.length>60?'...':''}</p>}
              {u.birthday&&<p className="admin-subs-row__bday">🎂 {new Date(u.birthday).toLocaleDateString('en-US',{month:'long',day:'numeric'})}</p>}
            </div>
            <select className="admin-tier-select" value={u.tier} style={{color:tc[u.tier]}}
              onChange={e=>{
                const newTier=e.target.value
                if(newTier===u.tier) return
                setPending({ member:u, oldTier:u.tier, newTier })
              }}>
              <option value="gold">Gold</option>
              <option value="premium">Premium</option>
              <option value="platinum">Platinum</option>
            </select>
            <div className="admin-subs-row__date">{new Date(u.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
          </div>
        ))}
        {!loading&&filtered.length===0&&<p style={{padding:'24px',color:'var(--muted)',fontSize:13,textAlign:'center'}}>No subscribers found</p>}
      </div>

      {pending && (
        <TierImpactModal
          member={pending.member}
          oldTier={pending.oldTier}
          newTier={pending.newTier}
          saving={saving}
          onCancel={()=>setPending(null)}
          onConfirm={confirmTierChange}
        />
      )}
    </div>
  )
}
